import React, { useState, useEffect } from 'react';
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaLock,
  FaUnlock,
  FaUserShield,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaExclamationTriangle,
  FaShieldAlt,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';
import { useAdminAuth } from '../context/AdminAuthContext';
import { API_ENDPOINTS } from '../../config/api';
import PermissionGate from './PermissionGate';

const AdminUserManagement = () => {
  const { token, user: currentUser } = useAdminAuth();
  
  // Helper: Check permission synchronously from user object
  const hasPermissionSync = (permission) => {
    if (!currentUser?.permissions || !Array.isArray(currentUser.permissions)) return false;
    return currentUser.permissions.includes(permission);
  };
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'support',
    mfaMethod: 'email',
    phone: '',
    password: '',
    status: 'active'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, roleFilter, statusFilter]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'list' })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load users');
      }

      if (data.success && data.users) {
        setUsers(data.users);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone?.toLowerCase().includes(query)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleCreateUser = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess('');

    // Frontend validation
    if (!formData.email || !formData.name || !formData.password) {
      setError('Please fill in all required fields.');
      setActionLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      setActionLoading(false);
      return;
    }

    // Password validation (Cognito default requirements)
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setActionLoading(false);
      return;
    }

    // Check for password complexity (at least one uppercase, one lowercase, one number, one special char)
    const hasUpperCase = /[A-Z]/.test(formData.password);
    const hasLowerCase = /[a-z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);
    const hasSpecialChar = /[^A-Za-z0-9]/.test(formData.password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.');
      setActionLoading(false);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'create',
          email: formData.email,
          name: formData.name,
          role: formData.role,
          mfaMethod: formData.mfaMethod,
          phone: formData.phone || undefined,
          password: formData.password
        })
      });

      const data = await response.json();
      if (!response.ok) {
        // Include details if available for better error messages
        const errorMsg = data.error || 'Failed to create user';
        const errorDetails = data.details ? `: ${data.details}` : '';
        throw new Error(`${errorMsg}${errorDetails}`);
      }

      if (data.success) {
        setSuccess('User created successfully');
        setShowCreateModal(false);
        resetForm();
        loadUsers();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error creating user:', err);
      console.error('Full error response:', err);
      
      let errorMessage = err.message || 'Failed to create user';
      
      // Provide more specific error messages based on common Cognito errors
      if (errorMessage.includes('already exists') || errorMessage.includes('User already exists') || errorMessage.includes('An account with the given email already exists')) {
        errorMessage = 'A user with this email already exists.';
      } else if (errorMessage.includes('password') || errorMessage.includes('Password') || errorMessage.includes('PasswordPolicyException')) {
        errorMessage = 'Password does not meet requirements. Password must be at least 8 characters and contain uppercase, lowercase, numbers, and special characters.';
      } else if (errorMessage.includes('InvalidParameterException') || errorMessage.includes('Invalid email')) {
        errorMessage = 'Invalid email format. Please check the email address and try again.';
      } else if (errorMessage.includes('Cognito') || errorMessage.includes('UserPool')) {
        // Show the actual error details if available
        if (errorMessage.includes(':')) {
          errorMessage = errorMessage.split(':').slice(-1)[0].trim() || 'Failed to create user account. Please check the email format and try again.';
        } else {
          errorMessage = 'Failed to create user account. Please check the email format and try again.';
        }
      } else if (errorMessage.includes('Insufficient permissions')) {
        errorMessage = 'You do not have permission to create users.';
      }
      
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    setError(null);
    setSuccess('');

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'update',
          email: selectedUser.email,
          name: formData.name,
          phone: formData.phone || undefined,
          mfaMethod: formData.mfaMethod
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user');
      }

      if (data.success) {
        setSuccess('User updated successfully');
        setShowEditModal(false);
        resetForm();
        loadUsers();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeRole = async (userEmail, newRole) => {
    setActionLoading(true);
    setError(null);
    setSuccess('');

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'change-role',
          email: userEmail,
          role: newRole
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to change role');
      }

      if (data.success) {
        setSuccess('Role changed successfully');
        loadUsers();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error changing role:', err);
      setError(err.message || 'Failed to change role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeStatus = async (userEmail, newStatus) => {
    setActionLoading(true);
    setError(null);
    setSuccess('');

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'change-status',
          email: userEmail,
          status: newStatus
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to change status');
      }

      if (data.success) {
        setSuccess(`User ${newStatus === 'suspended' ? 'suspended' : 'activated'} successfully`);
        loadUsers();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error changing status:', err);
      const errorMessage = err.message || 'Failed to change status';
      if (errorMessage.includes('own status') || errorMessage.includes('your own')) {
        setError('You cannot change your own status. Please ask another administrator.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (userEmail) => {
    if (!confirm('Are you sure you want to reset this user\'s password? They will need to set a new password on next login.')) {
      return;
    }

    setActionLoading(true);
    setError(null);
    setSuccess('');

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'reset-password',
          email: userEmail
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      if (data.success) {
        setSuccess('Password reset successfully. User will need to set a new password on next login.');
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    setError(null);
    setSuccess('');

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'delete',
          email: selectedUser.email
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete user');
      }

      if (data.success) {
        setSuccess('User deleted successfully');
        setShowDeleteModal(false);
        setSelectedUser(null);
        loadUsers();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
    setError(null);
    setSuccess('');
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email || '',
      name: user.name || '',
      role: user.role || 'support',
      mfaMethod: user.mfaMethod || 'email',
      phone: user.phone || '',
      password: '',
      status: user.status || 'active'
    });
    setShowEditModal(true);
    setError(null);
    setSuccess('');
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      role: 'support',
      mfaMethod: 'email',
      phone: '',
      password: '',
      status: 'active'
    });
    setSelectedUser(null);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-[#3c3836] text-[#fabd2f] border border-[#fabd2f]',
      admin: 'bg-[#3c3836] text-[#83a598] border border-[#83a598]',
      support: 'bg-[#3c3836] text-[#b8bb26] border border-[#b8bb26]',
      viewer: 'bg-[#3c3836] text-[#bdae93] border border-[#504945]'
    };
    return colors[role] || colors.viewer;
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'bg-[#3c3836] text-[#b8bb26] border border-[#b8bb26]',
      suspended: 'bg-[#3c3836] text-[#fb4934] border border-[#fb4934]',
      inactive: 'bg-[#3c3836] text-[#7c6f64] border border-[#504945]'
    };
    return colors[status] || colors.inactive;
  };

  const getRoleIcon = (role) => {
    if (role === 'super_admin') return <FaShieldAlt />;
    if (role === 'admin') return <FaUserShield />;
    return <FaUser />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#fabd2f] mx-auto mb-4"></div>
          <p className="text-[#bdae93]">Loading admin users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#ebdbb2] mb-2">Admin User Management</h1>
          <p className="text-[#bdae93]">Manage admin users, roles, and permissions</p>
        </div>
        <PermissionGate requiredPermission="admin_users:write">
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] transition-colors flex items-center space-x-2 font-medium"
          >
            <FaPlus size={16} />
            <span>Create User</span>
          </button>
        </PermissionGate>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-[#3c3836] border border-[#b8bb26] text-[#d5e39f] px-4 py-3 rounded-lg flex items-center space-x-2">
          <FaCheckCircle className="text-[#b8bb26]" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="bg-[#3c3836] border border-[#fb4934] text-[#fb8071] px-4 py-3 rounded-lg flex items-center space-x-2">
          <FaExclamationTriangle className="text-[#fb4934]" />
          <span>{error}</span>
        </div>
      )}

      {/* Filters */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-[#7c6f64]" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
            />
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
          >
            <option value="all">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="support">Support</option>
            <option value="viewer">Viewer</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Refresh Button */}
          <button
            onClick={loadUsers}
            className="px-4 py-2 bg-[#3c3836] text-[#ebdbb2] rounded-lg hover:bg-[#504945] transition-colors border border-[#504945]"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1d2021] border-b border-[#3c3836]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#ebdbb2] uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#ebdbb2] uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#ebdbb2] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#ebdbb2] uppercase tracking-wider">
                  MFA Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#ebdbb2] uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#ebdbb2] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#282828] divide-y divide-[#3c3836]">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-[#bdae93]">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.email || user.userId} className="hover:bg-[#3c3836] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-[#fabd2f] to-[#fe8019] rounded-full flex items-center justify-center text-[#1d2021] font-bold">
                          {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-[#ebdbb2]">{user.name || 'N/A'}</div>
                          <div className="text-sm text-[#bdae93]">{user.email}</div>
                          {user.phone && (
                            <div className="text-xs text-[#7c6f64] flex items-center space-x-1">
                              <FaPhone size={10} />
                              <span>{user.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        <span className="mr-1">{getRoleIcon(user.role)}</span>
                        {user.role?.replace('_', ' ').toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(user.status)}`}>
                        {user.status === 'active' ? <FaCheckCircle className="mr-1" size={12} /> : <FaTimesCircle className="mr-1" size={12} />}
                        {user.status?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#bdae93]">
                      {user.mfaMethod?.toUpperCase() || 'EMAIL'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#bdae93]">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <PermissionGate requiredPermission="admin_users:write">
                          <button
                            onClick={() => openEditModal(user)}
                            className="text-[#83a598] hover:text-[#fabd2f] transition-colors"
                            title="Edit"
                          >
                            <FaEdit size={16} />
                          </button>
                        </PermissionGate>
                        <PermissionGate requiredPermission="admin_users:write">
                          {currentUser?.email !== user.email && (
                            user.status === 'active' ? (
                              <button
                                onClick={() => handleChangeStatus(user.email, 'suspended')}
                                className="text-[#fabd2f] hover:text-[#fe8019] transition-colors"
                                title="Suspend"
                                disabled={actionLoading}
                              >
                                <FaLock size={16} />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleChangeStatus(user.email, 'active')}
                                className="text-[#b8bb26] hover:text-[#d5e39f] transition-colors"
                                title="Activate"
                                disabled={actionLoading}
                              >
                                <FaUnlock size={16} />
                              </button>
                            )
                          )}
                        </PermissionGate>
                        <PermissionGate requiredPermission="admin_users:write">
                          <button
                            onClick={() => handleResetPassword(user.email)}
                            className="text-[#83a598] hover:text-[#fabd2f] transition-colors"
                            title="Reset Password"
                            disabled={actionLoading}
                          >
                            <FaShieldAlt size={16} />
                          </button>
                        </PermissionGate>
                        <PermissionGate requiredPermission="admin_users:write">
                          <button
                            onClick={() => openDeleteModal(user)}
                            className="text-[#fb4934] hover:text-[#fb8071] transition-colors"
                            title="Delete"
                          >
                            <FaTrash size={16} />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1d2021] border border-[#3c3836] rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#3c3836]">
              <h2 className="text-2xl font-bold text-[#ebdbb2]">Create Admin User</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                  Email <span className="text-[#fb4934]">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                  Name <span className="text-[#fb4934]">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                  Role <span className="text-[#fb4934]">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                >
                  <option value="support">Support</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                  {hasPermissionSync('admin_users:write') && (
                    <option value="super_admin">Super Admin</option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                  MFA Method <span className="text-[#fb4934]">*</span>
                </label>
                <select
                  value={formData.mfaMethod}
                  onChange={(e) => setFormData({ ...formData, mfaMethod: e.target.value })}
                  className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              {formData.mfaMethod === 'sms' && (
                <div>
                  <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                    Phone <span className="text-[#fb4934]">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                    placeholder="+61 4XX XXX XXX"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                  Password <span className="text-[#fb4934]">*</span>
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                  required
                  minLength={8}
                />
                <p className="text-xs text-[#bdae93] mt-1">
                  Must be at least 8 characters with uppercase, lowercase, number, and special character
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-[#3c3836] flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-[#504945] rounded-lg text-[#ebdbb2] hover:bg-[#3c3836] transition-colors bg-[#282828]"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                disabled={actionLoading || !formData.email || !formData.name || !formData.password}
              >
                {actionLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1d2021] border border-[#3c3836] rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#3c3836]">
              <h2 className="text-2xl font-bold text-[#ebdbb2]">Edit Admin User</h2>
              <p className="text-sm text-[#bdae93] mt-1">{selectedUser.email}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                  Name <span className="text-[#fb4934]">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleChangeRole(selectedUser.email, e.target.value)}
                  className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent disabled:opacity-50"
                  disabled={!hasPermissionSync('admin_users:write')}
                >
                  <option value="support">Support</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                  {hasPermissionSync('admin_users:write') && (
                    <option value="super_admin">Super Admin</option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                  MFA Method
                </label>
                <select
                  value={formData.mfaMethod}
                  onChange={(e) => setFormData({ ...formData, mfaMethod: e.target.value })}
                  className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              {formData.mfaMethod === 'sms' && (
                <div>
                  <label className="block text-sm font-medium text-[#ebdbb2] mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                    placeholder="+61 4XX XXX XXX"
                  />
                </div>
              )}
            </div>
            <div className="p-6 border-t border-[#3c3836] flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-[#504945] rounded-lg text-[#ebdbb2] hover:bg-[#3c3836] transition-colors bg-[#282828]"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                disabled={actionLoading || !formData.name}
              >
                {actionLoading ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1d2021] border border-[#3c3836] rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-[#3c3836]">
              <h2 className="text-2xl font-bold text-[#ebdbb2]">Delete Admin User</h2>
            </div>
            <div className="p-6">
              <p className="text-[#ebdbb2] mb-4">
                Are you sure you want to delete <strong className="text-[#fabd2f]">{selectedUser.name || selectedUser.email}</strong>? This action cannot be undone.
              </p>
              <div className="bg-[#3c3836] border border-[#fabd2f] rounded-lg p-4 mb-4">
                <p className="text-sm text-[#fabd2f]">
                  <FaExclamationTriangle className="inline mr-2" />
                  This will permanently delete the user from both Cognito and DynamoDB.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-[#3c3836] flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 border border-[#504945] rounded-lg text-[#ebdbb2] hover:bg-[#3c3836] transition-colors bg-[#282828]"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="px-4 py-2 bg-[#fb4934] text-white rounded-lg hover:bg-[#fb8071] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;
